import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, FileText, DollarSign, Plus, Mail, Clock, X, AlertCircle, Settings, ChevronRight } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface AgencyDetails {
  id: number;
  name: string;
  abn: string;
  abnEntityName: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  billingEmail: string;
  status: string;
  currentLeaseCount: number;
  logoUrl?: string;
  createdAt: string;
}

interface SubscriptionCost {
  planName: string;
  leaseCount: number;
  pricePerLease: number;
  monthlyCost: number;
}

interface AgencyUser {
  id: number;
  email: string;
  name: string;
  role: string;
  active: boolean;
  isAgencyAdmin: boolean;
  createdAt: string;
}

interface Invitation {
  id: number;
  email: string;
  name: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  acceptedAt?: string;
}

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [agency, setAgency] = useState<AgencyDetails | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionCost | null>(null);
  const [users, setUsers] = useState<AgencyUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'AGENT', message: '' });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [agencyRes, subscriptionRes, usersRes, invitesRes] = await Promise.all([
        api.get<AgencyDetails>('/agency/admin/agency'),
        api.get<SubscriptionCost>('/agency/admin/subscription'),
        api.get<AgencyUser[]>('/agency/admin/users'),
        api.get<Invitation[]>('/agency/admin/invitations')
      ]);

      setAgency(agencyRes);
      setSubscription(subscriptionRes);
      setUsers(usersRes);
      setInvitations(invitesRes);
    } catch (err: any) {
      setError(err.message || 'Failed to load agency data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email) {
      setError('Email is required');
      return;
    }

    setInviting(true);
    setError('');

    try {
      const response = await api.post<{ success: boolean; error?: string }>('/agency/admin/invitations', inviteForm);

      if (response.success) {
        setShowInviteModal(false);
        setInviteForm({ email: '', name: '', role: 'AGENT', message: '' });
        loadDashboardData();
      } else {
        setError(response.error || 'Failed to send invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (id: number) => {
    try {
      await api.delete(`/agency/admin/invitations/${id}`);
      setInvitations(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Failed to cancel invitation:', err);
    }
  };

  const handleResendInvite = async (id: number) => {
    try {
      await api.post(`/agency/admin/invitations/${id}/resend`, {});
      loadDashboardData();
    } catch (err) {
      console.error('Failed to resend invitation:', err);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role?.toUpperCase()) {
      case 'ADMIN': return 'bg-purple-100 text-purple-800';
      case 'AGENT': return 'bg-blue-100 text-blue-800';
      case 'OWNER': return 'bg-green-100 text-green-800';
      case 'RENTER': return 'bg-orange-100 text-orange-800';
      case 'CONTRACTOR': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role: string) => {
    switch (role?.toUpperCase()) {
      case 'AGENT': return 'Agent';
      case 'RENTER': return 'Tenant';
      case 'OWNER': return 'Owner';
      case 'CONTRACTOR': return 'Contractor';
      case 'ADMIN': return 'Admin';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Agency Dashboard"
        subtitle={agency?.name || 'Manage your agency'}
        icon={<Building2 className="w-8 h-8" />}
        backPath="/dashboard"
      >
        <button
          onClick={() => navigate('/agency/settings')}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          <Settings className="w-5 h-5" />
          Settings
        </button>
      </PageHeader>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">{error}</div>
          <button onClick={() => setError('')} className="ml-auto text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Leases</p>
              <p className="text-3xl font-bold text-primary">{subscription?.leaseCount || 0}</p>
            </div>
            <FileText className="w-10 h-10 text-primary/20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Team Members</p>
              <p className="text-3xl font-bold text-primary">{users.length}</p>
            </div>
            <Users className="w-10 h-10 text-primary/20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Subscription Plan</p>
              <p className="text-xl font-bold text-primary">{subscription?.planName || 'N/A'}</p>
            </div>
            <DollarSign className="w-10 h-10 text-primary/20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Monthly Cost</p>
              <p className="text-3xl font-bold text-secondary">${subscription?.monthlyCost?.toFixed(2) || '0.00'}</p>
            </div>
            <DollarSign className="w-10 h-10 text-secondary/20" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Team Members */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 text-sm"
            >
              <Plus className="w-4 h-4" />
              Invite
            </button>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {users.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No team members yet. Send an invitation to get started.
              </div>
            ) : (
              users.map(user => (
                <div key={user.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-medium">
                        {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.name || user.email}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                      {formatRole(user.role)}
                    </span>
                    {user.isAgencyAdmin && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                        Admin
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending Invitations */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Pending Invitations</h2>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {invitations.filter(i => i.status === 'PENDING').length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No pending invitations.
              </div>
            ) : (
              invitations.filter(i => i.status === 'PENDING').map(invite => (
                <div key={invite.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{invite.email}</p>
                        {invite.name && <p className="text-sm text-gray-500">{invite.name}</p>}
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(invite.role)}`}>
                      {formatRole(invite.role)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Expires {new Date(invite.expiresAt).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleResendInvite(invite.id)}
                        className="text-secondary hover:underline text-sm"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Subscription Details */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Subscription Details</h2>
          <button
            onClick={() => navigate('/agency/billing')}
            className="text-secondary hover:underline text-sm flex items-center gap-1"
          >
            View Billing <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500">Current Plan</p>
            <p className="text-lg font-semibold text-gray-900">{subscription?.planName || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Active Leases</p>
            <p className="text-lg font-semibold text-gray-900">{subscription?.leaseCount || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Rate per Lease</p>
            <p className="text-lg font-semibold text-gray-900">${subscription?.pricePerLease?.toFixed(2) || '0.00'}/month</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Estimated Monthly</p>
            <p className="text-lg font-semibold text-secondary">${subscription?.monthlyCost?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Invite Team Member</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="colleague@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                >
                  <option value="AGENT">Property Agent</option>
                  <option value="OWNER">Property Owner</option>
                  <option value="RENTER">Tenant</option>
                  <option value="CONTRACTOR">Contractor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Personal Message (Optional)</label>
                <textarea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                  placeholder="Add a personal note to your invitation..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={inviting || !inviteForm.email}
                className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


